import { LaserficheConfiguration } from "@/components/integrations/LaserficheConfiguration";
import { PageHeader } from "@/components/page";
import { useLaserficheListConnections } from "@/hooks/queryHooks/laserficheIntegrationHooks";
import { useTeamList } from "@/hooks/queryHooks/teamHooks";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { pullUrlParams } from "@/lib/urlParams";
import { objectEmpty } from "@repo/lib/objectEmpty";
import type { Team } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { ArrowBigRightDashIcon, SettingsIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const IntegrationLaserficheConfigPage = () => {
  const { tenantId, connectionTeamId } = useParams();
  const teamId = connectionTeamId === "default" ? undefined : connectionTeamId;
  const { new: isNew } = pullUrlParams(true);

  const { data: connections } = useLaserficheListConnections(tenantId ?? "");
  const { data: teams } = useTeamList(tenantId ?? "");

  const teamMap = useMemo(
    () =>
      teams?.reduce(
        (map, team) => ((map[team.id] = team), map),
        Object.create(null) as Record<string, Team>
      ) ?? Object.create(null),
    [teams]
  );

  const navigate = useNavigate();

  const tabs = connections ? Object.keys(connections) : [];
  if (tabs.includes("")) {
    tabs.splice(tabs.indexOf(""), 1);
    tabs.unshift("default");
  }

  useEffect(() => {
    if (isNew) return;
    if (!connections || !tenantId) return;

    if (objectEmpty(connections)) {
      navigate(new AppUrlPath(tenantId).getIntegrationPath("laserfiche"));
    }
  }, [connections, tenantId, navigate, isNew]);

  if (!connections) return <Spinner />;

  const handleManageConnections = () => {
    navigate(new AppUrlPath(tenantId ?? "").getIntegrationPath("laserfiche"));
  };

  const handleSendHistory = () => {
    navigate(
      new AppUrlPath(tenantId ?? "").getIntegrationPath("laserfiche") + "#push"
    );
  };

  return (
    <div className="flex flex-col flex-grow gap-5">
      <PageHeader
        title={
          "Laserfiche Integration: " +
          (connectionTeamId === "default"
            ? "Default"
            : teamMap[connectionTeamId ?? ""]?.name ?? "<Unknown Team>")
        }
      />

      <div className="flex flex-row gap-2 items-start w-full justify-between p-1 rounded-md bg-gray-100">
        <div className="flex flex-row gap-2 flex-wrap mt-1">
          {tabs.map((tab) => (
            <Button
              key={tab}
              variant={connectionTeamId === tab ? undefined : "ghost"}
              className="h-8"
              onClick={() => {
                navigate(
                  new AppUrlPath(tenantId ?? "").getIntegrationPath(
                    "laserfiche",
                    tab
                  )
                );
              }}
            >
              {tab === "default"
                ? "Default"
                : teamMap[tab]?.name ?? "<Unknown Team>"}
            </Button>
          ))}
        </div>

        <div className="grow" />

        <Button
          variant="ghost"
          className="px-3"
          onClick={handleManageConnections}
          tooltip="Manage Multiple Connections"
        >
          <SettingsIcon className="w-5 mr-2" /> Connections
        </Button>
        <Button
          variant="ghost"
          className="px-3"
          onClick={handleSendHistory}
          tooltip="Push Historic Documents to Laserfiche"
        >
          <ArrowBigRightDashIcon className="w-5 mr-2" /> Push
        </Button>
      </div>

      {tenantId ? (
        <LaserficheConfiguration
          key={tenantId + ":" + (teamId ?? "default")}
          tenantId={tenantId}
          teamId={teamId}
        />
      ) : (
        <p>No tenant loaded</p>
      )}
    </div>
  );
};
